import { useApolloClient, useReactiveVar } from "@apollo/client";
import { Dialog } from "@mui/material";
import type { GraphQLError } from "graphql";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import InviteCanceled from "components/AuthOtp/InviteCanceled";
import InviteExpired from "components/AuthOtp/InviteExpired";
import InviteNotFound from "components/AuthOtp/InviteNotFound";
import OtpForm from "components/AuthOtp/OtpForm";
import { mapInviteLocaleToLanguage } from "components/AuthOtp/utils/mapInviteLocaleToLanguage";
import LoadingScreen from "components/shared/LoadingScreen";
import {
  ExternalPagePublicationStatusEnum,
  ExternalPageTypeEnum,
  useInviteQuery,
  useRequestOtpMutation,
  useValidateOtpMutation,
} from "generated/graphqlExternal";
import { ExternalSessionLossEnum, externalSessionLossVar } from "settings/apolloExternal/session";
import i18n from "translations/i18n";

import type { IExternalSessionGuardProps } from "../types";

/**
 * The external counterpart of `AuthenticationGuard`: only renders `component` with a valid OTP session for the
 * invite in the URL. Besides the invite's `sessionValid`, any request that reports the session lost (see
 * `externalSessionLossVar`) brings the access code back — on top of the page, so nothing typed is unmounted.
 */
const ExternalSessionGuard: React.FC<IExternalSessionGuardProps> = ({ component: Component }) => {
  const { token = "" } = useParams<{ token: string }>();
  const client = useApolloClient();

  const [isLanguageReady, setIsLanguageReady] = useState(false);
  const hasRequestedOtp = useRef(false);
  const hasRetriedNotFound = useRef(false);
  const sessionLoss = useReactiveVar(externalSessionLossVar);

  const { data, loading } = useInviteQuery({ variables: { token } });
  const invite = data?.invite;
  const { locale, type, status, closesAt, canceledAt, sessionValid, email } = invite ?? {};

  const inviteType = type ?? ExternalPageTypeEnum.BookingQuotation;
  const hasInvite = Boolean(invite);

  const [requestOtp, { loading: resending }] = useRequestOtpMutation();
  const [validateOtp, { loading: validating, error: validateError, reset: resetValidateOtp }] =
    useValidateOtpMutation();

  const canAccess =
    !!status && [ExternalPagePublicationStatusEnum.Open, ExternalPagePublicationStatusEnum.Closed].includes(status);

  const needsOtp = isLanguageReady && canAccess && (!sessionValid || sessionLoss !== null);

  // The code e-mail goes out in the invite's language, so nothing is requested before switching to it
  useEffect(() => {
    if (!hasInvite) {
      return;
    }

    const languageChanged = locale ? i18n.changeLanguage(mapInviteLocaleToLanguage(locale)) : Promise.resolve();

    languageChanged.then(() => setIsLanguageReady(true)).catch(() => setIsLanguageReady(true));
  }, [hasInvite, locale]);

  useEffect(() => {
    if (!needsOtp) {
      hasRequestedOtp.current = false;

      return;
    }

    if (!hasRequestedOtp.current) {
      hasRequestedOtp.current = true;
      resetValidateOtp();
      requestOtp({ variables: { token } }).catch(() => undefined);
    }
  }, [needsOtp, requestOtp, resetValidateOtp, token]);

  useEffect(
    () => () => {
      externalSessionLossVar(null);
    },
    [],
  );

  const handleSubmit = (code: string): void => {
    validateOtp({ variables: { token, code } })
      .then(result => {
        if (!result.data?.validateOtp) {
          return;
        }

        hasRetriedNotFound.current = sessionLoss === ExternalSessionLossEnum.NotFound;
        externalSessionLossVar(null);

        // Once every page query went through again the episode is over, so a later not_found gets a new code
        return client.refetchQueries({ include: "active" }).then(() => {
          if (externalSessionLossVar() === null) {
            hasRetriedNotFound.current = false;
          }
        });
      })
      .catch(() => undefined);
  };

  const handleResend = (): void => {
    resetValidateOtp();
    requestOtp({ variables: { token } }).catch(() => undefined);
  };

  if ((loading && !data) || (hasInvite && !isLanguageReady)) {
    return <LoadingScreen />;
  }

  // A failed refetch keeps the previous invite, so only a missing one means the link is invalid
  if (!invite || !status) {
    return <InviteNotFound />;
  }

  // A new OTP did not help either: the token was removed or the session belongs to another e-mail
  if (sessionLoss === ExternalSessionLossEnum.NotFound && hasRetriedNotFound.current) {
    return status === ExternalPagePublicationStatusEnum.Closed ? (
      <InviteExpired
        closesAt={closesAt}
        inviteType={inviteType}
      />
    ) : (
      <InviteNotFound />
    );
  }

  if (status === ExternalPagePublicationStatusEnum.Canceled) {
    return (
      <InviteCanceled
        canceledAt={canceledAt}
        inviteType={inviteType}
      />
    );
  }

  // Draft (not published yet) or any other status that never opens the page
  if (!canAccess) {
    return <InviteNotFound />;
  }

  const otpForm = (
    <OtpForm
      email={email ?? ""}
      error={(validateError?.graphQLErrors[0] as (GraphQLError & { code: string }) | undefined) ?? null}
      inviteType={inviteType}
      loading={validating}
      onResend={handleResend}
      onSubmit={handleSubmit}
      resending={resending}
    />
  );

  if (!sessionValid) {
    return otpForm;
  }

  return (
    <>
      <Component
        token={token}
        invite={invite}
      />

      <Dialog
        open={needsOtp}
        fullWidth
        maxWidth="sm"
        slotProps={{ paper: { sx: { backgroundColor: "transparent", backgroundImage: "none", boxShadow: "none" } } }}
      >
        {otpForm}
      </Dialog>
    </>
  );
};

export default ExternalSessionGuard;
